"use client";

import { ReactNode, useRef, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { IPageContent } from "@/types/pageContent.types";
import { motion } from "framer-motion";

import styles from "./styles.module.scss";
import TrackingDeviceOverlay from "./TrackingDeviceOverlay";

const SECTION1_STEP_COUNT = 20;

export type IonTrackingDeviceViewProps = {
  children?: ReactNode;
  ionTrackingDevice3dSection1Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSection1"];
  ionTrackingDevice3dSection2Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSection2"];
  ionTrackingDevice3dSectionHoriCardsData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSectionHoriCards"];
  ionTrackingDevice3dSectionPennyScaleData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSectionPennyScale"];
  ionTrackingDevice3dSection5Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSection5"];
  ionTrackingDevice3dSectionCoreCapabilitiesData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevice3dSectionCoreCapabilities"];
  ionTrackingDeviceImplementationProcessData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDeviceImplementationProcess"];

  ionTrackingDevicePag2Section1Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section1"];
  ionTrackingDevicePag2Section2Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section2"];
  ionTrackingDevicePag2Section3Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section3"];
  ionTrackingDevicePag2Section4Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section4"];
  ionTrackingDevicePag2Section5Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section5"];
  ionTrackingDevicePag2Section6Data: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDevicePag2Section6"];

  ionTrackingDeviceAppliedAcrossComplexData: IPageContent["ionTrackingDevicePageModel"]["ionTrackingDevicePage"]["ionTrackingDeviceAppliedAcrossComplex"];
  footerData: IPageContent["footerModelV15"]["footer"];
};

// Tracking Device : 3D Model
const TrackingDeviceModel = dynamic(() => import("./TrackingDeviceModel"), {
  ssr: false,
  loading: () => (
    <div className={styles.modelViewport}>
      <div className={styles.modelFallback}>Loading 3D model...</div>
    </div>
  ),
});

const TrackingDeviceView: React.FC<IonTrackingDeviceViewProps> = ({
  children,
  ionTrackingDevice3dSection1Data,
  ionTrackingDevice3dSection2Data,
  ionTrackingDevice3dSectionHoriCardsData,
  ionTrackingDevice3dSectionPennyScaleData,
  ionTrackingDevice3dSection5Data,
  ionTrackingDevice3dSectionCoreCapabilitiesData,
  ionTrackingDeviceImplementationProcessData,

  ionTrackingDevicePag2Section1Data,
  ionTrackingDevicePag2Section2Data,
  ionTrackingDevicePag2Section3Data,
  ionTrackingDevicePag2Section4Data,
  ionTrackingDevicePag2Section5Data,
  ionTrackingDevicePag2Section6Data,

  ionTrackingDeviceAppliedAcrossComplexData,
  footerData
}) => {
  const section1OverlayScrollRef = useRef<HTMLDivElement>(null);
  const [isModelReady, setIsModelReady] = useState(false);


  return (
    <div id="IonTrackingDeviceID">
      {/* ---------- 3D Model Section 1... ---------- */}
      <section className={styles.trackingDeviceView}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.modelContainer}>
            <motion.div
              className={styles.modelMotionWrapper}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
            >
              <TrackingDeviceModel
                src={ionTrackingDevice3dSection1Data.gallery[0].url}
                alt={ionTrackingDevice3dSection1Data.gallery[0].alt}
                externalScrollRef={section1OverlayScrollRef}
                onModelReady={() => setIsModelReady(true)}
              />
              <TrackingDeviceOverlay 
                ref={section1OverlayScrollRef} 
                stepCount={SECTION1_STEP_COUNT}
                isModelReady={isModelReady}
                ionTrackingDevice3dSection1Data={ionTrackingDevice3dSection1Data}
                ionTrackingDevice3dSection2Data={ionTrackingDevice3dSection2Data}
                ionTrackingDevice3dSectionHoriCardsData={ionTrackingDevice3dSectionHoriCardsData}
                ionTrackingDevice3dSectionPennyScaleData={ionTrackingDevice3dSectionPennyScaleData}
                ionTrackingDevice3dSection5Data={ionTrackingDevice3dSection5Data}
                ionTrackingDevice3dSectionCoreCapabilitiesData={ionTrackingDevice3dSectionCoreCapabilitiesData}
                ionTrackingDeviceImplementationProcessData={ionTrackingDeviceImplementationProcessData}

                ionTrackingDevicePag2Section1Data={ionTrackingDevicePag2Section1Data}
                ionTrackingDevicePag2Section2Data={ionTrackingDevicePag2Section2Data}
                ionTrackingDevicePag2Section3Data={ionTrackingDevicePag2Section3Data}
                ionTrackingDevicePag2Section4Data={ionTrackingDevicePag2Section4Data}
                ionTrackingDevicePag2Section5Data={ionTrackingDevicePag2Section5Data}
                ionTrackingDevicePag2Section6Data={ionTrackingDevicePag2Section6Data}

                ionTrackingDeviceAppliedAcrossComplexData={ionTrackingDeviceAppliedAcrossComplexData}
                footerData={footerData}
              >
                {children}
              </TrackingDeviceOverlay>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrackingDeviceView;
